/**
 * ProfileDraftBar — docked row for the live build_profile card (revamp T24),
 * the thesis bar's twin: dimension fill progress, tap to open
 * ProfileDraftSheet. The saved state flips the row to a check label; the ✕
 * dismisses the bar locally (the card stays in the transcript — the bar is a
 * surface, not the store; a reload replays it until the profile_saved
 * receipt lands).
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { profileProgress } from '../../services/profileCard';
import type { ProfileCard } from '../../services/chatStream';

export function ProfileDraftBar({
  card,
  saved,
  onOpen,
  onDismiss,
}: {
  card: ProfileCard;
  saved: boolean;
  onOpen: () => void;
  onDismiss?: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { filled, total } = profileProgress(card);

  return (
    <View
      testID="profile-draft-bar"
      style={[
        styles.row,
        { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
      ]}
    >
      <Pressable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={t('chat.profileDraft', { defaultValue: 'Profile draft' })}
        style={styles.main}
      >
        <Text style={[styles.symbol, { color: theme.colors.primary }]}>
          👤 {card.style_name ?? t('chat.profileDraft', { defaultValue: 'Profile draft' })}
        </Text>
        {saved ? (
          <Text style={[styles.meta, { color: theme.colors.success }]}>
            ✓ {t('chat.profileSaved', { defaultValue: 'Profile saved' })}
          </Text>
        ) : (
          <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
            {t('chat.profileDraft', { defaultValue: 'Profile draft' })} ◔ {filled}/{total} ·{' '}
            {t('chat.tapToView', { defaultValue: 'Tap to view' })}
          </Text>
        )}
      </Pressable>
      <Pressable
        testID="profile-draft-dismiss"
        onPress={onDismiss}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={t('actions.dismiss', { defaultValue: 'Dismiss' })}
        style={styles.dismiss}
      >
        <Text style={[styles.dismissText, { color: theme.colors.textTertiary }]}>✕</Text>
      </Pressable>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      borderTopWidth: 1,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    main: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
    },
    symbol: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    meta: {
      fontSize: theme.typography.fontSizeSm,
      flexShrink: 1,
    },
    dismiss: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    dismissText: {
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
